import PropTypes from 'prop-types';
import './style.scss';

import { useEffect, useMemo, useState } from 'react';

import { useForm, useFormState } from 'react-hook-form';
import { Button } from 'primereact/button';
import { useMutation, useQuery } from 'react-query';
import { useParams } from 'react-router-dom';
import { yupResolver } from '@hookform/resolvers/yup';

import { useTranslation } from 'react-i18next';
import { createFinancialAdjustmentSchema } from 'features/Project/validation';
import { getProjectById } from 'apis/project.api';

import { createFinancialAdjustment, updateFinancialAdjustment } from 'apis/financialAdjustment.api';
import { getExpenditureById } from 'apis/projectProposals.api';

import { TOAST } from 'constant';
import { toast } from 'layout';
import { mapError } from 'utils/func';

import Loading from 'components/Loading';
import { defaultFinancialAdjustment } from 'features/Project/defaultValues';
import FinancialAdjustmentForm from '../../components/FinancialAdjustmentForm';

const formOptions = { resolver: yupResolver(createFinancialAdjustmentSchema) };

function CreateFinancialAdjustment({ readOnly }) {
  // #region Data
  const { t } = useTranslation();
  const { projectId } = useParams();
  const [financialAdjustmentId, setFinancialAdjustmentId] = useState(null);

  const { control, handleSubmit, watch, setValue, reset } = useForm({
    mode: 'onChange',
    ...formOptions,
    shouldFocusError: true,
    defaultValues: defaultFinancialAdjustment,
  });

  const { errors, isDirty, dirtyFields } = useFormState({
    control,
  });

  const {
    data: projectData,
    isLoading: isProjectDataLoading,
    refetch,
  } = useQuery(['project', projectId], () => getProjectById(projectId));
  const project = useMemo(() => projectData?.data, [projectData]);

  const { data: expenditureData, isLoading: isExpenditureLoading } = useQuery(
    ['projectProposal-expenditure', project?.projectProposal?.id],
    () => getExpenditureById(project?.projectProposal?.id),
    { enabled: !!project?.projectProposal?.id }
  );

  const { mutateAsync: createMutate, isLoading: isCreateLoading } =
    useMutation(createFinancialAdjustment);

  const { mutateAsync: updateMuteAsync, isLoading: isUpdatingLoading } =
    useMutation(updateFinancialAdjustment);

  const onSubmit = async (body) => {
    try {
      if (financialAdjustmentId) {
        await updateMuteAsync({ body: { ...body, projectId }, id: financialAdjustmentId });
        toast(TOAST.SUCCESS, t('financialAdjustment.notification.update'));
      } else {
        const { data } = await createMutate({ ...body, projectId });
        setFinancialAdjustmentId(data?.id);
        toast(TOAST.SUCCESS, t('financialAdjustment.notification.create'));
      }
      refetch();
      reset({}, { keepValues: true });
    } catch (error) {
      toast(TOAST.ERROR, mapError(t, error));
    }
  };

  const checkKeyDown = (e) => {
    if (e.code === 'Enter') e.preventDefault();
  };

  const footerConfirmationPrompt = (onConfirm, onCancel) => (
    <div>
      <Button
        label={t('projectProposal.cancel')}
        className="w-7rem"
        severity="danger"
        onClick={onCancel}
        autoFocus
      />

      <Button
        label={t('projectProposal.ok')}
        className="w-7rem"
        severity="info"
        onClick={onConfirm}
      />
    </div>
  );

  useEffect(() => {
    const expenditureItems = expenditureData?.data?.proposedExpenditureSummaries?.map(
      ({ expenditureFromVNUHCM, expenditureFromFunding, proposedExpenditureType }) => ({
        expenditureTypeId: proposedExpenditureType?.id,
        name: proposedExpenditureType?.name,
        beforeExpenditureFromVNUHCM: expenditureFromVNUHCM,
        beforeExpenditureFromFunding: expenditureFromFunding,
        afterExpenditureFromVNUHCM: expenditureFromVNUHCM,
        afterExpenditureFromFunding: expenditureFromFunding,
        reason: '',
        subProposedExpenditureTypeId: proposedExpenditureType?.subProposedExpenditureTypeId,
      })
    );

    setValue('financialAdjustmentItems', expenditureItems);
    setValue('otherExpenditure', project?.projectProposal?.fundingExpenditureOther || 0);
  }, [project, expenditureData]);

  if (isProjectDataLoading || isExpenditureLoading) {
    return <Loading />;
  }

  return (
    <div className="financial-adjustment-container">
      <FinancialAdjustmentForm
        control={control}
        errors={errors}
        watch={watch}
        reset={reset}
        refetch={refetch}
        setValue={setValue}
        handleSubmit={handleSubmit(onSubmit)}
        checkKeyDown={checkKeyDown}
        isDirty={isDirty}
        dirtyFields={dirtyFields}
        footerConfirmationPrompt={footerConfirmationPrompt}
        project={project}
        readOnly={readOnly}
        isSendMailToOwner={project?.isSentEmail}
        projectId={projectId}
        hasContractFile={!project?.fileUrl}
        financialAdjustmentId={financialAdjustmentId}
      />
      {(isCreateLoading || isUpdatingLoading) && <Loading />}
    </div>
  );
}

CreateFinancialAdjustment.propTypes = {
  readOnly: PropTypes.bool,
};

CreateFinancialAdjustment.defaultProps = { readOnly: false };

export default CreateFinancialAdjustment;
